import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { TargetAndTransition } from 'framer-motion';
import { useBriefDock, type BriefDockState } from '@/app/providers/BriefDockContext';
import {
  BRIEF_DOCK_LAYOUT_ID,
  CHAT_DOCK_SPRING,
  CHAT_DOCK_SETTLE_FALLBACK_MS,
} from '@/common/motion/chatDockMotion';
import './HeaderBriefLauncher.css';

/** How long the landing effects (pulse ring + sparkles) stay mounted. */
const LANDED_FX_MS = 700;

interface HeaderBriefLauncherProps {
  /** Pending review-count badge (rendered on the bell when closed). */
  reviewCount?: number;
  /**
   * Fired once when the bell finishes its shrink-into-header flight — MainLayout
   * uses it to ripple-nudge the neighboring header icons.
   */
  onLanded?: () => void;
}

/**
 * Header notifications bell — the docked (closed) state of the Brief dock
 * (the panel it opens is the ActivityCenter "Notifications" panel; #5275).
 *
 * Shares `layoutId` with BriefDockFlight inside MainLayout's <LayoutGroup>: when
 * the Brief closes, this bell mounts as the flight surface unmounts and
 * framer-motion FLIPs it from the panel rect into the header slot (the panel
 * shrinks into the bell); clicking it sends the surface flying back out into the
 * panel (the bell grows into the Brief). Mirrors HeaderChatLauncher.
 *
 * The wrap is a COLLAPSIBLE slot (header order stays [Chat][Bell][Theme]): while
 * the Brief is open the slot animates to 0 width so the chat launcher packs over
 * next to Theme; when the Brief starts closing ('docking') it re-expands during
 * the 70ms liftoff gate so the neighbors slide apart and the bell lands into a
 * settled slot. Timing lives in HeaderBriefLauncher.css (KEEP IN SYNC).
 */
export function HeaderBriefLauncher({ reviewCount, onLanded }: HeaderBriefLauncherProps) {
  const dock = useBriefDock();
  // Collapsed while the Brief is open/opening; expanded at rest AND during the
  // close flight ('docking') so the slot is full-size by the time the bell
  // mounts at 'closed' (the flight lands on the slot's settled position).
  const slotEmpty = dock.dockState === 'undocking' || dock.dockState === 'open';
  return (
    <span className={`header-brief-launcher-wrap${slotEmpty ? ' is-empty' : ''}`}>
      {/* AnimatePresence snapshots `custom` into the exiting child's presence
          context at the removal render. openBrief flips dockState in that same
          render, so the exit variant sees the fresh state. */}
      <AnimatePresence custom={dock.dockState} initial={false}>
        {dock.dockState === 'closed' && (
          <BellButton key="header-brief-bell" reviewCount={reviewCount} onLanded={onLanded} />
        )}
      </AnimatePresence>
    </span>
  );
}

/**
 * Exit variant for the bell's unmount paths:
 * - 'undocking' → the unmount IS the flight handoff (BriefDockFlight mounts in
 *   the same commit and FLIPs from this slot) — leave instantly, no effects.
 * - anything else (instant close under reduced motion / placement change) → a
 *   quick fade so the icon doesn't pop.
 * Exported for tests only.
 */
export function bellExit(reducedMotion: boolean) {
  return (dockState: BriefDockState): TargetAndTransition => {
    if (dockState === 'undocking') {
      // Invisible from the first hand-off frame: framer keeps the exiting button
      // alive briefly for the shared-layoutId transfer and projects it alongside
      // the growing flight — at opacity 1 its glyph would show as a giant icon
      // inside the expanding container.
      return { opacity: 0, transition: { duration: 0 } };
    }
    return {
      opacity: 0,
      transition: { duration: reducedMotion ? 0.12 : 0.15, ease: 'easeOut' },
    };
  };
}

function BellButton({ reviewCount, onLanded }: HeaderBriefLauncherProps) {
  const { pendingLandingFx, ackLandingFx, openBrief } = useBriefDock();
  const prefersReducedMotion = useReducedMotion() ?? false;
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // 'flying'  = traveling from the panel (wears the panel surface, glyph hidden)
  // 'landed'  = touchdown effects running (pulse ring, sparkles)
  // 'idle'    = plain header bell button
  // 'handoff' = open click committed, glyph hidden — see handleClick
  const [phase, setPhase] = useState<'flying' | 'landed' | 'idle' | 'handoff'>(() =>
    pendingLandingFx && !prefersReducedMotion ? 'flying' : 'idle',
  );
  const landedRef = useRef(false);

  const handleLanded = useCallback(() => {
    if (landedRef.current) return;
    landedRef.current = true;
    ackLandingFx();
    // Closing the panel returns focus to the bell once it lands.
    buttonRef.current?.focus();
    if (prefersReducedMotion) {
      setPhase('idle');
    } else {
      setPhase('landed');
      onLanded?.();
    }
  }, [ackLandingFx, onLanded, prefersReducedMotion]);

  // Landing driver. With motion enabled, onLayoutAnimationComplete is the
  // primary signal and the timeout is the can't-wedge fallback; with reduced
  // motion (or a restored mount that still has the flag) land immediately.
  useEffect(() => {
    if (!pendingLandingFx || landedRef.current) return;
    if (phase === 'flying') {
      const t = window.setTimeout(handleLanded, CHAT_DOCK_SETTLE_FALLBACK_MS);
      return () => window.clearTimeout(t);
    }
    handleLanded();
  }, [pendingLandingFx, phase, handleLanded]);

  // Retire the touchdown effects.
  useEffect(() => {
    if (phase !== 'landed') return;
    const t = window.setTimeout(() => setPhase('idle'), LANDED_FX_MS);
    return () => window.clearTimeout(t);
  }, [phase]);

  const handleClick = () => {
    // Hide the glyph in its own SYNCHRONOUS commit before the unmount: the
    // exiting button shares the morph layoutId, so framer keeps it alive for the
    // whole flight and projects it along the path — AnimatePresence freezes its
    // LAST committed render, and a visible glyph there rides the expansion as a
    // ghost icon (the crossfade system owns opacity for shared elements, so an
    // exit-variant opacity can't kill it).
    flushSync(() => setPhase('handoff'));
    openBrief();
  };

  return (
    <motion.button
      ref={buttonRef}
      type="button"
      className={`app-header-activities header-brief-bell${
        phase === 'flying' ? ' is-flying' : ''
      }${phase === 'landed' ? ' is-landed' : ''}${phase === 'handoff' ? ' is-handoff' : ''}`}
      title="Notifications"
      aria-label="Notifications"
      aria-haspopup="dialog"
      aria-expanded={false}
      aria-controls="daily-brief-panel"
      onClick={handleClick}
      // Reduced motion: no shared-element flight — just a quick fade.
      layoutId={prefersReducedMotion ? undefined : BRIEF_DOCK_LAYOUT_ID}
      // This transition governs the CLOSE shrink (panel → bell): on close this
      // bell mounts as the flight unmounts and framer FLIPs it from the panel
      // rect into the header slot. Both directions now use CHAT_DOCK_SPRING so
      // the Brief dock's open AND close match the chat dock exactly (the OPEN
      // grow is BriefDockFlight, which also reuses CHAT_DOCK_SPRING).
      transition={prefersReducedMotion ? { duration: 0.15 } : CHAT_DOCK_SPRING}
      initial={prefersReducedMotion ? { opacity: 0 } : false}
      animate={prefersReducedMotion ? { opacity: 1 } : undefined}
      variants={{ exit: bellExit(prefersReducedMotion) }}
      exit="exit"
      onLayoutAnimationComplete={phase === 'flying' ? handleLanded : undefined}
      style={{ position: 'relative' }}
    >
      {/* Glass for the flight: WebKit can drop backdrop-filter when it shares an
          element with mask-image, so the mask sits on the button and the blur on
          this surface span (styled only while flying). */}
      <span className="header-brief-bell-surface" aria-hidden="true" />
      <svg
        className="header-brief-bell-icon"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      {(reviewCount ?? 0) > 0 && <span className="activities-badge">{reviewCount}</span>}
      <span className="header-brief-bell-pulse" aria-hidden="true" />
      <span className="header-brief-bell-sparkles" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    </motion.button>
  );
}
